import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { breadcrumbSchema, collectionSchema } from "@/lib/seo";

export const SampleBadge = ({ className = "" }) => (
  <span className={`inline-flex items-center gap-1 font-mono-tech text-[9px] uppercase tracking-[0.25em] text-amber-300/90 border border-amber-400/30 bg-amber-400/10 px-2 py-1 ${className}`} data-testid="sample-badge">
    Sample project · illustrative
  </span>
);

export const CaseStudyCard = ({ cs, index = 0 }) => (
  <Reveal delay={Math.min(index * 0.05, 0.3)}>
    <Link to={`/portfolio/${cs.slug}`} data-testid={`case-card-${cs.slug}`} className="group block bg-[#080808] hover:bg-[#0C0C0C] border border-zinc-900 hover:border-[#C87455]/50 transition-colors duration-500 h-full">
      <div className="relative overflow-hidden border-b border-zinc-900">
        {cs.cover && <img src={cs.cover} alt={cs.title} loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-[1.03]" />}
        {cs.sample && <SampleBadge className="absolute top-3 left-3 backdrop-blur" />}
      </div>
      <div className="p-6">
        <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#C87455]">{[cs.industry, cs.location].filter(Boolean).join(" · ")}</p>
        <h3 className="font-serif-display text-2xl text-white mt-3 leading-tight">{cs.title}</h3>
        <p className="text-zinc-400 text-sm leading-relaxed mt-3 line-clamp-3">{cs.summary}</p>
        <div className="flex items-center justify-between mt-5">
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">{cs.quantity}</span>
          <ArrowUpRight size={14} className="text-zinc-600 group-hover:text-[#C87455] transition-colors" />
        </div>
      </div>
    </Link>
  </Reveal>
);

export default function Portfolio() {
  const [cases, setCases] = useState(null);
  useEffect(() => { api.get("/case-studies").then((r) => setCases(r.data)).catch(() => setCases([])); }, []);

  return (
    <main data-testid="portfolio-page" className="pt-40 pb-32 min-h-screen">
      <Seo
        title="Corporate Engraving Portfolio & Case Studies — Leduc & Edmonton | Ember & Steel Studio"
        description="Corporate gifting and branded merchandise project write-ups from Ember & Steel Studio in Leduc, Alberta: engraved drinkware, coordinated gift packages and custom metal work for Edmonton-area businesses. Entries marked 'Sample project' are illustrative examples."
        path="/portfolio"
        jsonLd={[
          breadcrumbSchema([["Home", "/"], ["Portfolio", "/portfolio"]]),
          collectionSchema({ name: "Corporate engraving portfolio", description: "Project write-ups and illustrative package examples from Ember & Steel Studio.", path: "/portfolio", items: (cases || []).map((c) => [c.title, `/portfolio/${c.slug}`]) }),
        ]}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">Portfolio · Corporate work</span>
          <h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl text-white mt-4 tracking-tight">
            Case studies <span className="italic text-[#A9B0B7]">from the bench</span>
          </h1>
          <p className="text-zinc-400 mt-6 max-w-2xl leading-relaxed">
            How Edmonton-area businesses have used engraved drinkware, coordinated gift packages and custom metal pieces for teams, clients and partners — the brief, what we made, and how it was delivered.
          </p>
        </Reveal>

        {cases && cases.some((c) => c.sample) && (
          <p className="mt-10 border border-amber-400/20 bg-amber-400/5 text-amber-200/80 text-xs leading-relaxed px-4 py-3 max-w-3xl" data-testid="sample-notice">
            Entries marked “Sample project” are illustrative examples of our corporate packages, not real client engagements. Real case studies are added as projects are completed and approved for publication.
          </p>
        )}

        {cases === null ? (
          <p className="font-mono-tech text-xs text-zinc-600 mt-16">Loading…</p>
        ) : cases.length === 0 ? (
          <p className="font-mono-tech text-xs text-zinc-600 mt-16" data-testid="portfolio-empty">Case studies are coming soon. In the meantime, see <Link to="/gallery" className="text-[#C87455]">finished pieces in the gallery</Link>.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14" data-testid="portfolio-grid">
            {cases.map((cs, i) => <CaseStudyCard key={cs.id} cs={cs} index={i} />)}
          </div>
        )}

        <Reveal>
          <div className="mt-24 border border-zinc-900 bg-[#0A0A0A] p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <h2 className="font-serif-display text-3xl text-white">Planning a corporate order?</h2>
              <p className="text-zinc-400 text-sm mt-3 max-w-xl">Tell us the headcount, budget and date. We reply with a quotation, confirmed availability and a digital proof before anything is engraved.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/business" data-testid="portfolio-business-cta" className="bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-6 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em]">Corporate packages</Link>
              <Link to="/quote?type=business" data-testid="portfolio-quote-cta" className="border border-zinc-700 text-zinc-200 hover:border-[#C87455] transition-colors px-6 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em]">Request a quote</Link>
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
